import React, {createRef, memo, useContext, useEffect, useState} from "react";

import './SidebarParticipants.scss'
import {ConferenceUser, ConferenceUserWithStatus, UserFromToken} from "@inpriton/types";
import UserBadge from "../general/UserBadge";
import Sidebar from "../Sidebar";
import AppContext from "../../context/AppContext";
import {useTranslation} from "react-i18next";
import SidebarRow from "./inc/SidebarRow";
import MaterialIcon from "../icons/MaterialIcon";
import useWebRtcClient from "../../webrtc/WebRtcClient";
import Visualization from "../general/Visualization";
import MediaContext from "../../context/MediaContext";
import UserStatusSymbols from "../general/UserStatusSymbols";
import InConfirm from "../general/InConfirm";
import NotificationContext from "../../context/NotificationContext";
import {notifyWsFailure} from "../../lib/notifyWsFailure";
import {isWaiting} from "../../helper/presence";

const UserListItem = (props: {
  user: ConferenceUserWithStatus,
  onAdmit: (uuid: string) => void,
  onRemove: (uuid: string) => void,
}) => {
  const { user } = props

  const {t} = useTranslation()

  const {
    userData,
    userVolumes,
    setUserVolumes,
  } = useContext(AppContext)

  const {
    volume: ownVolume,
  } = useContext(MediaContext)

  const onAdmit = (e: any, uuid: string) => {
    e.preventDefault()
    props.onAdmit(uuid)
  }

  // RISKS.md/P1.19: moderator "remove user" (kick) — revokes the target's sessions server-side.
  const onRemove = (e: any, uuid: string) => {
    e.preventDefault()
    props.onRemove(uuid)
  }

  const isOwn = user.uuid === userData?.uuid

  const userVolume = isOwn ? 1 : userVolumes[user.uuid]

  const setVolume = (volume: number) => {
    setUserVolumes({...userVolumes, [user.uuid]: volume})
  }

  return (
    <>
      <div className="details">
       <div className="left">
         <div className="badge">
           <UserBadge name={user.name || user.email}/>
           <div className={`user-online-status ${user.online ? 'online' : 'offline'}`}></div>
         </div>
         <div className="username">
           {user.name || user.email}
           {user.isModerator ? (
             <div className="user-info">{t('userType.moderator')}</div>
           ) : null}
         </div>
         {userData?.isModerator && isWaiting(user) ? (
           <a href="#" className="link-admit" onClick={e => onAdmit(e, user.uuid)}>
             {t('sidebar.participants.admit')}
           </a>
         ) : null}
         {/* Host can remove any non-host user in ANY status — invited, waiting, or already
             admitted — not just admitted ones. Moderators/hosts are exempt (!user.isModerator). */}
         {userData?.isModerator && !isOwn && !user.isModerator ? (
           <a href="#" className="link-remove" onClick={e => onRemove(e, user.uuid)}>
             {t('sidebar.participants.remove')}
           </a>
         ) : null}
         <UserStatusSymbols user={user}/>
       </div>

        <div className="right">
          { !isOwn && user.online ? (
            <div className="volume-buttons">
              <button onClick={() => setVolume(userVolume !== .1 ? .1 : 1)}
                      className={userVolume === .1 ? 'active' : ''}>
                {new MaterialIcon('volume_down').get()}
              </button>
              <button onClick={() => setVolume(userVolume !== 0 ? 0 : 1)} className={userVolume === 0 ? 'active' : ''}>
                {new MaterialIcon('volume_mute').get()}
              </button>
            </div>
          ) : null}
        </div>
      </div>
      {/*{user.admitted && user.online ? (*/}
      {/*  <Visualization max={10} value={volume}/>*/}
      {/*) : null}*/}

    </>
  )
}

const UserList = (props: {
  users: ConferenceUserWithStatus[],
  ownUuid: string,
  onAdmit: (uuid: string) => void,
  onRemove: (uuid: string) => void,
}) => {
  return (
    <ul className="user-list">
      {props.users.map(user => (
        <li key={user.uuid} className="user">
          <UserListItem user={user} onAdmit={props.onAdmit} onRemove={props.onRemove}/>
        </li>
      ))}
    </ul>
  )
}

export default () => {
  const {t} = useTranslation()

  const {
    userData,
    users,
    ws,
    currentRoom,
  } = useContext(AppContext)

  const { createNotification, hideNotification } = useContext(NotificationContext)

  // A kick is destructive and permanent, so gate it behind a confirmation. Holds the uuid
  // pending confirmation (null = no dialog); the actual ws.removeUser runs only on "Ja".
  const [confirmRemoveUuid, setConfirmRemoveUuid] = useState<string | null>(null)

  const groupedUsers = [{
    title: t('sidebar.participants.status.admitted'),
    attendees: users.filter(u => u.joined && u.admitted && !u.isTranslator),
    translators: users.filter(u => u.joined && u.admitted && u.isTranslator),
  }, {
    title: t('sidebar.participants.status.waiting'),
    attendees: users.filter(u => isWaiting(u) && !u.isTranslator),
    translators: users.filter(u => isWaiting(u) && u.isTranslator),
  },{
    // Everyone in neither group above: `!u.joined` alone would drop someone who waited and left.
    title: t('sidebar.participants.status.invited'),
    attendees: users.filter(u => !(u.joined && u.admitted) && !isWaiting(u) && !u.isTranslator),
    translators: users.filter(u => !(u.joined && u.admitted) && !isWaiting(u) && u.isTranslator),
  }]

  // @ts-ignore
  const ownUuid = userData.uuid as string

  // RISKS.md/P2.15: these must catch — a refused moderation action is indistinguishable from a slow
  // one, since the row only moves once the roster update lands, and on a refusal it never does.
  const onUserAdmit = async (uuid: string) => {
    try {
      if (!ws || !currentRoom) throw new Error('not connected')
      await ws.admitUser(currentRoom, uuid)
    } catch (e) {
      console.error('admit failed', e)
      notifyWsFailure({ createNotification, hideNotification }, e, {
        id: 'admit-failed',
        message: t('sidebar.participants.admitFailed'),
        ttl: 10000,
      })
    }
  }

  // RISKS.md/P1.19: kick — server revokes the target's refresh sessions so they can't re-join.
  const onUserRemove = async (uuid: string) => {
    try {
      if (!ws || !currentRoom) throw new Error('not connected')
      await ws.removeUser(currentRoom, uuid)
    } catch (e) {
      console.error('remove failed', e)
      notifyWsFailure({ createNotification, hideNotification }, e, {
        id: 'remove-failed',
        message: t('sidebar.participants.removeFailed'),
        ttl: 10000,
      })
    }
  }

  return (
    <>
    <Sidebar type="participant" title={t('sidebar.participants.title')}>
      <>
        {groupedUsers.filter(group => group.attendees.length + group.translators.length).map(group => (
          <SidebarRow
            key={group.title}
            title={group.title}
            indicator={group.attendees.length + group.translators.length}
            collapsable
          >
            <>
              {group.attendees.length ? (
                <SidebarRow
                  title={t('userType.attendees')}
                  collapsable
                >
                  <UserList
                    users={group.attendees}
                    ownUuid={ownUuid}
                    onAdmit={onUserAdmit}
                    onRemove={(uuid) => setConfirmRemoveUuid(uuid)}
                  />
                </SidebarRow>
              ) : null}
              {group.translators.length ? (
                <SidebarRow
                  title={t('userType.translators')}
                  collapsable
                >
                  <UserList
                    users={group.translators}
                    ownUuid={ownUuid}
                    onAdmit={onUserAdmit}
                    onRemove={(uuid) => setConfirmRemoveUuid(uuid)}
                  />
                </SidebarRow>
              ) : null}
            </>
          </SidebarRow>
        ))}
      </>
    </Sidebar>
    {confirmRemoveUuid ? (
      <InConfirm
        title={t('sidebar.participants.confirmRemove.title')}
        message={t('sidebar.participants.confirmRemove.message')}
        onConfirm={() => { onUserRemove(confirmRemoveUuid); setConfirmRemoveUuid(null) }}
        onCancel={() => setConfirmRemoveUuid(null)}
      />
    ) : null}
    </>
  )
}